import { Check, Plus } from 'lucide-react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

interface TermAddActionProps {
  readonly termId: string;
  readonly label: string;
  readonly excluded: boolean;
  readonly onAddTerm: (term: { readonly id: string; readonly label: string }) => void;
}

/**
 * Shared add-or-already-added affordance used by both the HPO browse tree
 * and the flat search-results list inside `HpoBrowseDialog`. Renders the
 * "Add" button when the term isn't already on the patient and a muted
 * "Already added" badge with a primary checkmark when it is.
 */
export function TermAddAction({
  termId,
  label,
  excluded,
  onAddTerm,
}: TermAddActionProps): ReactElement {
  const { t } = useTranslation();
  if (excluded) {
    return (
      <span className="shrink-0 inline-flex items-center gap-1 text-[11px] text-muted-foreground">
        <Check className="h-3.5 w-3.5 text-primary" aria-hidden="true" />
        {t('vocab.picker.alreadyAdded')}
      </span>
    );
  }
  return (
    <button
      type="button"
      onClick={() => {
        onAddTerm({ id: termId, label });
      }}
      className="shrink-0 inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-[11px] text-foreground hover:bg-accent"
    >
      <Plus className="h-3 w-3" aria-hidden="true" />
      {t('patient.phenotypes.browse.add')}
    </button>
  );
}
